import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles, type Article } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'news');
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(articles.filter((a) => a.category === activeSlug));
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://plushy.cn/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory?.name || '',
                item: `https://plushy.cn/category/${activeSlug}`,
              },
            ],
          }),
        }}
      />

      <div className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory?.name || '' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6 md:mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })}
                  className={`px-4 py-2 rounded-full text-sm font-sans whitespace-nowrap transition-all duration-200 cursor-pointer flex items-center gap-1.5 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}